import {AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {Subscription, timer} from "rxjs";
import {delay, filter, finalize} from "rxjs/operators";
import scrollIntoView from "scroll-into-view-if-needed";

import {JASO, REGION2, REGION2_DEFAULT} from "../../shared/defines/gukeo.const";
import {
  GetRegion2Css,
  MENU_PAGE,
  PRELOAD_RATIO,
  REGION_INFO2_MAP,
  DIALECT_SCROLL_SPEED,
  LIST_SCROLL_INTERVAL,
  BODY_HEIGHT_VALUE,
  BODY_HEIGHT_CAL,
  EXT_HEIGHT_CAL,
  SIDE_HEIGHT_DELTA
} from "../../shared/defines/desktop.const";
import {
  BookmarkChange,
  DIALECT_SEARCH_PLACEHOLDER,
  DETAIL_POPUP_DELAY,
  HISTORY_KEY_SEARCH_DIALECT,
  HISTORY_KEY_VIEW_DIALECT,
  LIST_TYPE,
  SearchParam, UI_RELOAD_HEIGHT
} from "../../shared/defines/client.const";
import {SimpleDialect} from "../../shared/models/dialect.model";
import {
  GetSearchFromQuery,
  listItemId,
} from "../../shared/defines/client.func";
import {ListLoader} from "../../shared/interfaces/list-loader";
import {GlobalEventService} from "../../shared/services/global-event/global-event.service";
import {SearchBoxComponent} from "../../shared/components/search-box/search-box.component";
import {DialectService} from "../../shared/services/dialect/dialect.service";
import {DialectBookmarkService} from "../../shared/services/dialect/dialect-bookmark.service";
import {DialectHistoryService} from "../../shared/services/dialect/dialect-history.service";

const THIS_PAGE_ADDR = '/dialect-list';

@Component({
  selector: 'app-dialect-list-page',
  templateUrl: './dialect-list-page.component.html',
  styleUrls: ['./dialect-list-page.component.scss']
})
export class DialectListPageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container') container?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('reload') reload?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('innerScroll') innerScroll?: ElementRef; // ngAfterViewInit() 에서 사용 가능

  // 화면 사이즈 맞춤에 사용
  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild(SearchBoxComponent) extHeader?: SearchBoxComponent;
  @ViewChild('extBody') extBody?: ElementRef;
  @ViewChild('extBody2') extBody2?: ElementRef;

  protected readonly HISTORY_KEY_SEARCH_DIALECT = HISTORY_KEY_SEARCH_DIALECT;
  protected readonly HISTORY_KEY_VIEW_DIALECT = HISTORY_KEY_VIEW_DIALECT;
  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly listItemId = listItemId;

  protected selectedMenu: MENU_PAGE = MENU_PAGE.NONE;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  private type: LIST_TYPE | null = null;

  // ListLoader 설정
  public ll: ListLoader<SimpleDialect> | null = null;
  private getNextList$: Subscription | null | undefined = null;
  private needReload = false;
  protected get lastId(): string | null {
    const ll = this.ll?.getList() ?? null;
    return ll ? ll[ll.length - 1].id : null;
  }

  private queryParams$?: Subscription;
  private params$?: Subscription;

  // 상세화면 표시 관련 정의
  public curDetail: string | null = null; // 현재 선택된 지방어의 id

  // 지역 선택 팝업창 관련
  public selectedRegion: REGION2 = REGION2_DEFAULT;  // 선택 지역

  protected get regionTitle(): string {
    return REGION_INFO2_MAP.get(this.selectedRegion)?.name ?? '전全&nbsp;지역';
  }

  // 자소 인덱스
  public curJasoIdx: JASO | null = null;

  // 검색창에 넣는 값
  public keyword: string | null = null;
  public placeholder: string | null = null;
  protected scrollTop = true;

  // 화면 크기 조정
  private bodyHeight = UI_RELOAD_HEIGHT;

  // 스크롤 처리
  private scrollDownInterval: any = null;

  protected showScrollDown = false;

  protected isNoResult(): boolean {
    return (this.ll?.IsComplete() ?? false) && (this.ll?.getList()?.length === 0 ?? false);
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private dialectSvc: DialectService,
    private bmSvc: DialectBookmarkService,
    private historySvc: DialectHistoryService,
    private globalEventSvc: GlobalEventService,
  ) {

    /******************************************************************************************
     * 라우터 파리미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * 처음 화면에 들어올 때, 라우터 주소 변견 핸들러보다 더 먼저 불려진다. (이상하지만 그렇다)
     *
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        this.queryParams = {...params};
        delete this.queryParams.serial;
        console.log('DialectComponent::queryParams =', this.type, params, this.queryParams);

        if (this.type) {
          // 라우터 주소 변경 핸들러로 들어와서 init()이 처음 불려진 경우 = 이미 화면이 목록이 불려진 경우 = 목록이 초기화되어있는 경우!
          // 이 조건을 안 걸면 init()가 두번 불려진다.

          // 현재 목록에서 지정한 단어 항목이 존재하는지 확인
          if (this.queryParams?.id) {
            // id가 있다는 의미는, 목록처리는 필요 없고, 상세창 처리만 하겠다는 의미.
            console.log('DialectComponent::queryParams.id =', this.queryParams.id);

            const processParams = () => {
              if (this.queryParams?.id) {
                if (this.ll?.getById(this.queryParams.id)) {
                  this.CloseDetail();
                  this.OpenDetail(this.queryParams.id);

                  this.scrollToDialect(this.queryParams.id);
                }
                else {
                  // 목록에 없는 단어를 선택한 경우, 서버에서 받아온다.
                  this.init();
                }
              }
              else {
                throw new Error('DialectComponent queryParams.id is null');
              }
            };

            if (this.ll?.IsLoading()) {
              // 이 시점에서 아직 목록 로딩이 안 끝났을 수 있다.
              this.ll.GetNextList().pipe(
                delay(DETAIL_POPUP_DELAY),
              ).subscribe(processParams);
            } else {
              processParams();
            }
          } else {
            // 다시 서버에서 받아온다.
            this.init();
          }
        }
      });

    /******************************************************************************************
     * 라우터 주소 변경 핸들러 - 라우터 주소 읽어오는 곳.
     * 라우터 주소에 포함된 파라미터가 변경되어도 불려진다. (여기서는 type만 변경되어도 불려짐)
     *
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.params$ = route.params.subscribe(params => {
      console.log('DialectComponent::params =', params, this.queryParams);
      this.type = params['type'];

      this.init();
    });
  }

  ngAfterViewInit(): void {
    this.updateExtHeight();

    // 최초 로딩 시, 화면 배치가 어그러지는 것을 고치기위한 코드. (TODO: 효과 있는지 확인 후 필요시 제거)
    timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }

  ngOnDestroy() {
    this.queryParams$?.unsubscribe();
    this.params$?.unsubscribe();
  }

  /**
   * 저장된 searchParam(라우팅에서 전달받은 옵션)을 이용해서 페이지를 재 초기화하고 다시 로딩한다.
   */
  private init(): void {
    let param: SearchParam;

    switch (this.type) {
      case LIST_TYPE.SERVER:
        // id 파라미터가 있는 경우 이력화면으로 다시 이동해서 표시한다.
        // if (this.queryParams?.id) {
        //   this.historySvc.addHistory(this.queryParams.id);
        //   this.router.navigate([THIS_PAGE_ADDR, LIST_TYPE.HISTORY], {
        //     queryParams: this.queryParams,
        //   }).then();
        //   return;
        // }

        this.selectedMenu = MENU_PAGE.DIALECT;
        this.ll = this.dialectSvc;
        this.placeholder = DIALECT_SEARCH_PLACEHOLDER;
        [param, this.curJasoIdx, this.keyword, this.selectedRegion] = GetSearchFromQuery(this.queryParams);
        // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
        // init() must preserve URL `start`; `id` only fills the anchor when `start` is absent.
        // 상세는 별도 핸들러(loadList)에서 queryParams.id로 복원한다.
        if (this.queryParams?.id && !this.queryParams.start) {
          console.warn(
            `DialectListPageComponent::init() start anchor missing; deriving from id=${this.queryParams.id}.`,
            { queryParams: this.queryParams },
          );
          param.start = this.queryParams.id;
        }
        console.log(`keyword=${this.keyword}, jasoIdx=${this.curJasoIdx}, param=${JSON.stringify(param)}`, this);
        this.ReloadList(param);
        break
      case LIST_TYPE.BOOKMARK:
        this.selectedMenu = MENU_PAGE.DIALECT_BOOKMARK;
        this.ll = this.bmSvc;
        this.curJasoIdx = null;
        this.placeholder = DIALECT_SEARCH_PLACEHOLDER;
        [param, this.curJasoIdx, this.keyword, this.selectedRegion] = GetSearchFromQuery(this.queryParams);
        console.log('LIST_TYPE.BOOKMARK', param, this.curJasoIdx, this.keyword);
        this.ReloadList(param);
        break;
      case LIST_TYPE.HISTORY:
        this.selectedMenu = MENU_PAGE.DIALECT_HISTORY;
        this.ll = this.historySvc;
        this.curJasoIdx = null;
        this.placeholder = DIALECT_SEARCH_PLACEHOLDER;
        param = (this.queryParams && 'id' in this.queryParams) ? { id: this.queryParams.id } : {};
        this.ReloadList(param);
        break;
      default:
        console.error('Invalid LIST_TYPE =', this.type);
    }
  }

  public OpenDetail(dialect_id: string, addHistory = true): void {
    this.curDetail = dialect_id;
    if (!(this.ll instanceof DialectHistoryService) && addHistory) {
      this.historySvc.addHistory(dialect_id);
      console.log('add history', this.historySvc);
    }
  }

  public CloseDetail(bookmark: boolean | null = null): void {
    this.curDetail = null;
  }


  /**
   * 마지막 child 의 View가 초기화 되었을 때 호출된다.
   * @param word
   */
  onChildViewInit(dialect: SimpleDialect) {
    // console.warn('onChildViewInit()', dialect, this.innerScroll?.nativeElement.offsetHeight, this.container?.nativeElement.offsetHeight);

    if (dialect.id === this.lastId) {
      timer(0).subscribe(() => {
        this.showScrollDown = this.innerScroll?.nativeElement.offsetHeight > this.container?.nativeElement.offsetHeight;
      });
    }
  }

  /****************************************************************************
   * 브라우저 크기 변경 처리.
   ****************************************************************************/


  // 'resize' 이벤트를 감지하고 이벤트 발생 시 updateHeight 메소드를 호출합니다.
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.updateExtHeight();
  }

  private updateExtHeight(): void {
    if (this.extContainer && this.extHeader) {
      const containerHeight = this.extContainer.nativeElement.offsetHeight;
      const headerHeight = this.extHeader.offsetHeight;
      this.bodyHeight = containerHeight - headerHeight - EXT_HEIGHT_CAL;

      if (this.extBody) {
        this.extBody.nativeElement.style.height = this.bodyHeight + 'px';
      }

      if (this.extBody2) {
        const bodyHeight2 = Math.max(
          BODY_HEIGHT_VALUE - SIDE_HEIGHT_DELTA,
          this.bodyHeight + BODY_HEIGHT_CAL);
        this.extBody2.nativeElement.style.setProperty('--side-height', bodyHeight2 + 'px');
      }
    }
  }

  /****************************************************************************
   * 지방어 목록 로딩 처리.
   ****************************************************************************/

  public onSelectDialect($event: MouseEvent, dialect_id: string) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    if (this.curDetail !== dialect_id && this.queryParams?.id === dialect_id) {
      // 단어 도움말이 열려있는데, 라우터에는 선택된 단어가 표시되고 있을 때
      this.OpenDetail(dialect_id);
    }
    else {
      const queryParams = {
        ...this.queryParams,
        id: dialect_id
      };
      // Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
      this.router.navigate([THIS_PAGE_ADDR, this.type], {queryParams}).then();
    }
  }

  public onSearch(param: SearchParam): void {
    console.log('DialectComponent::onSearch()', param);

    /**
     * 지역이 선택되었을 때, 자소가 선택되면 중첩되어 필터링 된다.
     * 지역이 선택되었을 때, 검색 입력하면 지역 적용은 해지된다.
     */
    const queryParams: SearchParam = {
      ...(this.queryParams && 'region2' in this.queryParams && !('keyword' in param)
        ? { region2: this.queryParams.region2 }
        : {}),
      ...param,
    };

    // 모든 검색은 사투리 목록에서 검색
    // NOTE: 이전에는 이력 화면, 북마크 내에서도 검색이 가능했음.
    this.router.navigate([THIS_PAGE_ADDR, LIST_TYPE.SERVER],
      { queryParams }).then();
  }

  public ReloadList(param: SearchParam): void {
    // console.warn('DialectComponent::ReloadList()', param, this.queryParams);

    this.showScrollDown = false;

    this.ll?.ResetList(param);
    this.loadList(true);

  }

  private loadList(cancel = false): void {
    // console.log('loadList()', this.queryParams);
    if (this.getNextList$) {
      console.warn('DialectComponent::loadList() already call loadList!');
      if (cancel) {
        this.getNextList$.unsubscribe();
      }
      else {
        return;
      }
    }

    if (this.ll?.IsComplete()) {
      console.warn('DialectComponent::loadList() already load all dialect!');
      return;
    }

    this.getNextList$ = this.ll?.GetNextList()
      .pipe(
        filter(first => first),
        // delay(DETAIL_POPUP_DELAY),
        finalize(() => {
          // console.warn('getNextList$ = null');
          this.getNextList$ = null;

          if (this.needReload) {
            timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        })
      ).subscribe(
        first => {

          if (this.ll?.getTotalCount() === 0) {
            this.CloseDetail();
          }
          else if (this.queryParams?.id) {
            // 바로 보기 상태라면, 서버로부터 로딩이 완료되고 나서 바로 상세 화면을 연다.
            // TODO: init() 쪽으로 코드를 옮기기.
            //  앱에서와 다르게 (목록과 상세 화면이 분리되어 있는 경우) 여기까지 와서 OpenDetail을 할 필요가 없어 보인다.
            const targetId = String(this.queryParams.id);
            if (!this.ll?.getById(targetId)) {
              console.warn(
                `DialectListPageComponent::loadList() id=${targetId} not in loaded list; OpenDetail will fetch separately.`,
                { listSize: this.ll?.getList()?.length ?? 0, queryParams: this.queryParams },
              );
            }
            this.OpenDetail(this.queryParams.id, true);
          }
          else {
            // 로딩 된 항목이 한개 이상이라면 첫번째 항목을 보여준다.
            const first = this.ll?.getList()?.at(0);
            first && this.OpenDetail(first.id, false);
          }
        }
      );
  }

  public onGoHistory(): void {
    this.type = null; // 생성자의 queryParams 쪽으로 init()가 불려지는 것을 막음.
    this.router.navigate([THIS_PAGE_ADDR, LIST_TYPE.HISTORY]).then();
  }


  /****************************************************************************
   * 책갈피 실시간 처리
   ****************************************************************************/

  protected onBookmarkChange(bookmarkChange: BookmarkChange): void {
    if (this.type === LIST_TYPE.BOOKMARK) {
      // 북마크 화면에서 북마크가 변경되면, 목록 다시 로딩
      this.init();
    }
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 단어를 자동 로딩한다.
   ****************************************************************************/

  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  protected onScroll($event: Event): void {
    if (this.container === undefined) {
      throw new Error('DialectComponent::onScroll() - #container does not exist!!');
    }

    if (this.ll && !this.ll.IsLoading() && !this.ll.IsComplete() && this.reload) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      const elContainer = this.container.nativeElement;
      if (tar !== elContainer) {
        console.error('DialectComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;
      // NOTE: 앱은 스크롤되는 윗 부분에 빈 공간이 있지만, 책상형은 공간이 없다. 그래서 innerHeight 반영하지 않는다.
      // console.warn('onScroll() ', elReload.offsetTop, this.bodyHeight, elContainer.scrollTop + this.bodyHeight);
      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        if (this.getNextList$ === null) {
          this.loadList();
        }
        else {
          // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
          this.needReload = true;
        }
      }

    }
  }

  onClickRope($event: MouseEvent) {
    console.warn('onClickRope()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.container?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
      // behavior: 'auto',
    });
  }

  onStartScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onStartScrollDown()', $event);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    // 한번은 바로 내려간다.
    this.container?.nativeElement.scrollTo({
      top: this.container.nativeElement.scrollTop + DIALECT_SCROLL_SPEED * 2,
      behavior: 'auto',
    });

    this.scrollDownInterval = setInterval(() => {
      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + DIALECT_SCROLL_SPEED,
        behavior: 'auto',
      });
    }, LIST_SCROLL_INTERVAL);
  }

  onEndScrollDown($event: MouseEvent|TouchEvent) {
    // console.warn('onEndScrollDown()', $event);
    clearInterval(this.scrollDownInterval);
    this.scrollDownInterval = null;
  }

  /**
   * 지방어를 선택하면, 왼쪽 목록에서 해당 단어가 보이도록 스크롤 시킨다.
   * @param dialect_id
   * @private
   */
  private scrollToDialect(dialect_id: string): void {
    const el: HTMLElement | null = this.document.getElementById(listItemId(dialect_id));

    if (el) {
      scrollIntoView(el, {
        behavior: 'smooth',
        block: 'start',
        scrollMode: 'if-needed',
      });
    }
    else {
      console.error('DialectPageComponent::scrollToDialect() - not found element', dialect_id);
    }
  }


}
